@if (subject(); as s) {
  @if (!poppedOut()) {
    <!-- The widget: a glass capsule, and when unfolded a solid card on the side away from its edge.
         `data-companion-avoid`: the Ask companion steps aside from it, as it steps aside from the companion. -->
    <div #widget class="widget" data-testid="flow-recording-widget" role="group" data-companion-avoid
      [attr.aria-label]="'shell.flow_widget.label' | transloco"
      [attr.data-state]="state()" [attr.data-mode]="s.mode" [attr.data-side]="position().side"
      [class.is-expanded]="expanded()" [class.is-dragging]="dragging()" [class.grows-up]="growsUp()"
      [class.is-phone]="phone()"
      [style.z-index]="zIndex" [style.left]="placeStyle().left" [style.right]="placeStyle().right"
      [style.top]="placeStyle().top" [style.bottom]="placeStyle().bottom" [style.--card-max.px]="cardMax()"
      (animate.enter)="arrive($event)" (animate.leave)="widgetOut($event)"
      (pointerdown)="onPointerDown($event)" (pointerenter)="onPointerEnter($event)" (pointerleave)="onPointerLeave($event)"
      (keydown)="onKeydown($event)">
      <ng-container *ngTemplateOutlet="capsule; context: { $implicit: false }" />

      @if (expanded()) {
        <!-- What somebody reads is solid (decision 20). The title, the lines and the note are meeting content. -->
        <section class="card" data-testid="flow-recording-widget-card" [id]="widgetId + '-card'">
          <p class="card__title ph-mask">{{ title() }}</p>
          @if (agenda.view(); as v) {
            <!-- Where the meeting is against its agenda (rdx P5.08): the live screen's strip, compact. -->
            <im-flow-live-agenda-strip class="card__agenda" [view]="v" [compact]="true" prefix="flow-widget" [badge]="false" [canOpen]="false" />
          }
          <div class="card__lines" data-testid="flow-recording-widget-lines">
            @for (line of lines(); track line.id) {
              <p class="line ph-mask">
                @if (line.speaker) { <span class="line__speaker">{{ line.speaker }}<im-flow-speaker-guess [show]="!!line.speakerGuess" /></span> }
                <span class="line__text">{{ line.text }}</span>
              </p>
            } @empty {
              <p class="line line--quiet">{{ 'shell.flow_widget.listening' | transloco }}</p>
            }
          </div>
          <form class="note" (submit)="addNote($event)">
            <label class="sr-only" [for]="widgetId + '-note'">{{ 'shell.flow_widget.note_label' | transloco }}</label>
            <input class="note__field ph-mask" type="text" autocomplete="off" data-testid="flow-recording-widget-note"
              [id]="widgetId + '-note'" [value]="noteDraft()" [disabled]="noteSaving()"
              [placeholder]="'shell.flow_widget.note_placeholder' | transloco" (input)="onNoteInput($event)" />
            <paper-button class="tool note__add" variant="ghost" shape="icon" size="sm" icon="send" type="submit"
              data-testid="flow-recording-widget-note-add" [disabled]="!noteDraft().trim() || noteSaving()" [loading]="noteSaving()"
              [ariaLabel]="'shell.flow_widget.note_add' | transloco" [title]="'shell.flow_widget.note_add' | transloco" />
          </form>
          <p class="note__status" role="status" [class.note__status--trouble]="noteStatus() === 'failed'">
            @if (noteStatus() === 'saved') { {{ 'shell.flow_widget.note_saved' | transloco }} }
            @if (noteStatus() === 'failed') { {{ 'shell.flow_widget.note_failed' | transloco }} }
          </p>
          @if (facts()?.pauseFailed) {
            <p class="card__trouble" role="alert">{{ 'shell.flow_widget.pause_failed' | transloco }}</p>
          }
          @if (state() === 'recording') {
            <!-- The recording notice is one-time (Nath, 14 Oct 2026): while recording, a small reminder. -->
            <p class="card__reminder" data-testid="flow-recording-widget-recording-reminder">{{ 'flow_live.recording_reminder' | transloco }}</p>
          }
          @if (stopNowShown()) {
            <p class="card__trouble" role="status" data-testid="flow-recording-widget-stop-flush">{{ 'flow_live.stop_flush.stalled' | transloco: { seconds: savingSeconds() } }}</p>
          }
          <paper-button class="card__open" variant="outline" shape="pill" size="sm" icon="arrow-up-right" iconPosition="right" [block]="true"
            data-testid="flow-recording-widget-open" (click)="open('expanded')">
            {{ 'shell.flow_widget.open' | transloco }}
          </paper-button>
        </section>
      }
    </div>
  }
}

<!-- The capsule, drawn in the page and, popped out, in the Picture-in-Picture window (`inPip`). -->
<ng-template #capsule let-inPip>
  <div class="capsule" data-testid="flow-recording-widget-capsule" [class.capsule--pip]="inPip" [class.has-line]="!inPip && liveLineVisible()" [attr.data-state]="state()">
    <!-- The body returns to the live page. Its arrow keys move the widget (in the page). -->
    <paper-button class="body" variant="ghost" shape="pill" size="sm" data-testid="flow-recording-widget-body"
      [ariaDescribedby]="inPip ? null : widgetId + '-hint'" (click)="onBodyClick($event, inPip)">
      <span class="sr-only">{{ (subject()?.mode === 'elsewhere' ? 'shell.flow_widget.open' : 'shell.flow_recording_return') | transloco }},</span>
      <!-- Flow's mark: expression only, the dot and the words say what is happening. -->
      <span class="mark" aria-hidden="true">
        <im-flow-mark [size]="markSize" [state]="markState()" [level]="markLevel()" [transition]="markTransition" />
      </span>
      <span class="dot" aria-hidden="true"></span>
      <span class="status">{{ stateKey() | transloco }}</span>
      <span class="time" role="timer">{{ elapsed() }}</span>
    </paper-button>
    @if (!inPip) {
      <span class="sr-only" [id]="widgetId + '-hint'">{{ 'shell.flow_widget.move_hint' | transloco }}</span>
    }

    @if (controls().pause) {
      <!-- Pause or Resume: its words and glyph follow the state. Never disabled while the change is in
           flight (a disabled button drops the focus; a second press does nothing), and a press from the
           keyboard keeps its place when the state flips (refocusPause). -->
      @if (state() === 'paused') {
        <paper-button class="tool" variant="ghost" shape="icon" size="sm" icon="play" data-testid="flow-recording-widget-pause"
          [loading]="!!facts()?.pausePending"
          [ariaLabel]="'shell.flow_widget.resume' | transloco" [title]="'shell.flow_widget.resume' | transloco"
          (click)="togglePause()" />
      } @else {
        <paper-button class="tool" variant="ghost" shape="icon" size="sm" icon="pause" data-testid="flow-recording-widget-pause"
          [loading]="!!facts()?.pausePending"
          [ariaLabel]="'shell.flow_widget.pause' | transloco" [title]="'shell.flow_widget.pause' | transloco"
          (click)="togglePause()" />
      }
    }
    @if (controls().resume) {
      <!-- flow-recording-recovery: my recording that nobody records (a reloaded or closed tab), resumed here. -->
      <paper-button class="tool tool--resume" variant="solid" tone="brand" shape="icon" size="sm" icon="mic" data-testid="flow-recording-widget-resume"
        buttonId="flow-recording-widget-resume" [loading]="recovery.busy() === subject()?.sessionId"
        [ariaLabel]="'shell.flow_widget.resume' | transloco" [title]="'shell.flow_widget.resume' | transloco"
        (click)="resumeHere()" />
    }
    @if (stopNowShown()) {
      <!-- tv45.154: saving has gone 30 s without progress. It carries on; this stops without waiting for it. -->
      <paper-button class="tool tool--stop-now" variant="outline" shape="pill" size="sm" data-testid="flow-recording-widget-stop-now"
        buttonId="flow-recording-widget-stop-now" [title]="'flow_live.stop_flush.stalled' | transloco: { seconds: savingSeconds() }"
        (click)="stopNow()">{{ 'flow_live.stop_flush.stop_now' | transloco }}</paper-button>
    }
    @if (controls().stop) {
      <paper-button class="tool tool--stop" variant="ghost" shape="icon" size="sm" icon="square" data-testid="flow-recording-widget-stop"
        [ariaLabel]="'shell.flow_widget.stop' | transloco" [title]="'shell.flow_widget.stop' | transloco"
        (click)="stop(inPip)" />
    }
    @if (compactOpen()) {
      <paper-button class="tool" variant="ghost" shape="icon" size="sm" icon="arrow-up-right" data-testid="flow-recording-widget-open-compact"
        [ariaLabel]="'shell.flow_widget.open' | transloco" [title]="'shell.flow_widget.open' | transloco"
        (click)="openFromCapsule(inPip)" />
    }
    @if (!inPip && controls().expand) {
      <paper-button class="tool" variant="ghost" shape="icon" size="sm" [icon]="expandIcon()" data-testid="flow-recording-widget-expand"
        [ariaExpanded]="expanded()" [ariaControls]="widgetId + '-card'"
        [ariaLabel]="(expanded() ? 'shell.flow_widget.collapse' : 'shell.flow_widget.expand') | transloco"
        (click)="toggleExpanded('button')" />
    }
    @if (!inPip && controls().popOut) {
      <paper-button class="tool" variant="ghost" shape="icon" size="sm" icon="window" data-testid="flow-recording-widget-pop-out"
        [ariaLabel]="'shell.flow_widget.pop_out' | transloco" [title]="'shell.flow_widget.pop_out' | transloco"
        (click)="popOut('button')" />
    }
    @if (inPip) {
      <paper-button class="tool" variant="ghost" shape="icon" size="sm" icon="x" data-testid="flow-recording-widget-pop-in"
        [ariaLabel]="'shell.flow_widget.pop_in' | transloco" [title]="'shell.flow_widget.pop_in' | transloco"
        (click)="popIn('button')" />
    } @else {
      <paper-button class="tool" variant="ghost" shape="icon" size="sm" icon="more-horizontal" data-testid="flow-recording-widget-menu"
        [ariaLabel]="'shell.flow_widget.more' | transloco" [title]="'shell.flow_widget.more' | transloco"
        [ariaHaspopup]="'menu'" [paperMenuTriggerFor]="menu" paperMenuPosition="above-end" />
    }

    <!-- D3: the newest line, on its own row under the controls, while the card is folded (the card has the full
         lines). The region stays while the line may run, so the first words are announced too; until they come
         it holds no row. Meeting content: masked, never in an attribute. -->
    @if (!inPip && liveLineShown()) {
      <div class="ticker" data-testid="flow-recording-widget-live-line" aria-live="polite" aria-atomic="true"
        [class.ticker--empty]="!liveLineVisible()">
        @for (line of liveLines(); track line.id) {
          <p class="ticker__line ph-mask">
            @if (line.speaker) { <span class="ticker__speaker">{{ line.speaker }}<im-flow-speaker-guess [show]="!!line.speakerGuess" /></span> }
            <span class="ticker__text">{{ line.text }}</span>
          </p>
        }
      </div>
    }
  </div>
</ng-template>

<paper-context-menu #menu [items]="menuItems()" (action)="onMenu($event)" />

<p class="sr-only" aria-live="polite">{{ announcement() }}</p>
